import { useEffect, useMemo, useState } from "react";
import { Col, Form as BTSForm, InputGroup, Modal, Row } from "react-bootstrap";
import GradientButton from "../../../../Core/GradientButton";
import AlertModal from "../../../../Core/AlertModal";
import PinCodeBoxes from "../../../../Core/PinCodeBoxes";
import { useSessionStore } from "../../../../../Stores/SessionStore";

const RESEND_COOLDOWN_SECONDS = 60;

function digitsOnly(input) {
	return (input || "").replace(/\D/g, "");
}

function localPart(phoneNumber) {
	const digits = digitsOnly(phoneNumber);
	if (digits.length === 11 && digits.startsWith("27")) return digits.slice(2);
	if (digits.length === 10 && digits.startsWith("0")) return digits.slice(1);
	return digits;
}

function isValidLocalPhoneNumber(input) {
	const digits = digitsOnly(input);
	return /^(?:\d{9}|0\d{9})$/.test(digits);
}

export default function ChangeNumberModal({ show, onClose }) {
	const store = useSessionStore();
	const customer = store.CustomerDetails?.Customer;
	const currentNumber = customer?.CellphoneNumber ?? "";
	const currentLocal = useMemo(() => localPart(currentNumber), [currentNumber]);

	const [newNumber, setNewNumber] = useState("");
	const [otp, setOtp] = useState("");
	const [otpSent, setOtpSent] = useState(false);
	const [sending, setSending] = useState(false);
	const [saving, setSaving] = useState(false);
	const [resendSeconds, setResendSeconds] = useState(0);
	const [error, setError] = useState(null);
	const [info, setInfo] = useState(null);
	const [alert, setAlert] = useState(null);

	const newLocal = useMemo(() => localPart(newNumber), [newNumber]);
	const newNumberOk = isValidLocalPhoneNumber(newNumber) && newLocal.length > 0 && newLocal !== currentLocal;
	const sendOtpEnabled = newNumberOk && !sending && resendSeconds === 0 && !saving;
	const showSaveChanges = otpSent && otp.length === 4;

	useEffect(() => {
		if (!show) {
			setNewNumber("");
			setOtp("");
			setOtpSent(false);
			setResendSeconds(0);
			setError(null);
			setInfo(null);
		}
	}, [show]);

	useEffect(() => {
		if (resendSeconds <= 0) return undefined;
		const timer = setInterval(() => {
			setResendSeconds((previous) => Math.max(0, previous - 1));
		}, 1000);
		return () => clearInterval(timer);
	}, [resendSeconds]);

	async function handleSendOtp() {
		if (!sendOtpEnabled) return;

		setError(null);
		setInfo(null);
		setOtp("");
		setSending(true);
		const result = await store.CustomersService.RequestChangeNumberOtp(newLocal);
		setSending(false);

		if (result?.Success) {
			setOtpSent(true);
			setResendSeconds(RESEND_COOLDOWN_SECONDS);
			setInfo("We sent a 4-digit code to your new number. Enter it below to confirm.");
			return;
		}

		setError(result?.Message ?? "Failed to send OTP. Please try again.");
	}

	async function handleSaveChanges() {
		if (!showSaveChanges || saving) return;

		setError(null);
		setSaving(true);
		const result = await store.CustomersService.ChangePhoneNumber(newLocal, otp);
		setSaving(false);

		if (result?.Success) {
			await store.LoadCustomerDetails();
			setAlert({
				show: true,
				success: true,
				title: "Number Updated",
				message: "Your WhatsApp number has been updated successfully.",
				onClose: () => {
					setAlert(null);
					onClose();
				},
			});
			return;
		}

		setError(result?.Message ?? "Failed to update your number. Please try again.");
	}

	const sendOtpTitle = sending
		? "Sending..."
		: otpSent && resendSeconds > 0
		? `Resend OTP (${resendSeconds}s)`
		: otpSent
		? "Resend OTP"
		: "Send OTP";

	return (
		<>
			<Modal show={show} onHide={onClose} size="lg">
				<Modal.Header>
					<h3>Change WhatsApp Number</h3>
				</Modal.Header>
				<Modal.Body>
					<BTSForm.Group as={Row} className="mb-3">
						<BTSForm.Label column sm={4}>Current Number</BTSForm.Label>
						<Col sm={8}>
							<InputGroup>
								<InputGroup.Text>+27</InputGroup.Text>
								<BTSForm.Control value={currentLocal || "(no number on file)"} readOnly disabled className="bg-light"/>
							</InputGroup>
						</Col>
					</BTSForm.Group>
					<BTSForm.Group as={Row} className="mb-3">
						<BTSForm.Label column sm={4}>New Number</BTSForm.Label>
						<Col sm={8}>
							<InputGroup>
								<InputGroup.Text>+27</InputGroup.Text>
								<BTSForm.Control
									value={newNumber}
									onChange={(event) => {
										setNewNumber(digitsOnly(event.target.value));
										setError(null);
										setInfo(null);
										setOtp("");
										setOtpSent(false);
										setResendSeconds(0);
									}}
									maxLength={10}
									inputMode="numeric"
									placeholder="e.g. 0812345678"
								/>
							</InputGroup>
						</Col>
					</BTSForm.Group>
					<p className="text-muted">An OTP will be sent to the new WhatsApp number to verify the change.</p>
					<GradientButton
						title={sendOtpTitle}
						variant="greenYellow"
						onClick={handleSendOtp}
						disabled={!sendOtpEnabled}
						loading={sending}
					/>
					{newNumberOk && (
						<div className="mt-4 text-center">
							<BTSForm.Label>Verification Code</BTSForm.Label>
							<PinCodeBoxes value={otp} onChange={setOtp} disabled={saving} />
						</div>
					)}
					{info && <small className="text-success d-block mt-3">{info}</small>}
					{error && <small className="text-danger d-block mt-3">{error}</small>}
					{showSaveChanges && (
						<GradientButton
							title={saving ? "Saving..." : "Save Changes"}
							variant="greenYellow"
							onClick={handleSaveChanges}
							disabled={saving}
							loading={saving}
							className="mt-3"
						/>
					)}
					<GradientButton
						title="Cancel"
						variant="yellowOrange"
						onClick={onClose}
						disabled={saving || sending}
						className="mt-2"
					/>
				</Modal.Body>
			</Modal>
			{alert && <AlertModal modal={alert} />}
		</>
	);
}
